import { z } from 'zod';
import { PayPeriodConfigSchema } from './pay-period.js';
import { COACH_EMAIL_TEXT_MAX, unknownCoachEmailVariables } from './coach-email.js';
import { NullableBusinessId } from './tax.js';
// The four class-pay calculation methods, mirroring Arbox's classes-summary
// report options. The report computes the money; this selects which counting
// basis it uses.
export const ClassCalcMethodSchema = z.enum([
    'checkin', // by check-in
    'registered', // by registered
    'checkin_with_late_cancel', // by check-in + late cancels
    'registered_with_late_cancel', // by registered + late cancels
]);
// LEGACY: a box used to run ONE salary method, chosen here. Superseded by the
// independent mech_* flags below (a box may now enable any combination), kept
// only as the migration-037 backfill's join key and a safety net for any code
// path not yet switched over. Do not add new reads of this field.
export const BoxTypeSchema = z.enum(['group_classes', 'hugim']);
// The three independently-toggleable pay mechanisms a box can run, any
// combination at once (see staff_mechanisms for the per-staff assignment):
//   'group_classes' — the existing per-meeting class-pay feature
//   'hugim'         — coach paid a % of participant payments to a חוג (course)
//   'time_clock'    — regular employees paid off Arbox time-clock attendance
//                      (base salary + travel + bonus/tax; no class/hugim pay)
export const BoxMechanismSchema = z.enum(['group_classes', 'hugim', 'time_clock']);
/** The box's currently-enabled mechanisms, as a list. */
export function enabledMechanisms(s) {
    const out = [];
    if (s.mech_group_classes)
        out.push('group_classes');
    if (s.mech_hugim)
        out.push('hugim');
    if (s.mech_time_clock)
        out.push('time_clock');
    return out;
}
/** The box's single active mechanism, for dispatch — a box currently runs
 *  exactly ONE mechanism for every staff member (like the legacy box_type),
 *  the same as today; per-staff multi-mechanism consolidation is a separate,
 *  later piece of work (see staff_mechanisms, not yet wired into dispatch).
 *  Priority mirrors the existing box_type precedent (hugim already took
 *  priority over the group_classes default): hugim > time_clock > group_classes. */
export function primaryMechanism(s) {
    if (s.mech_hugim)
        return 'hugim';
    if (s.mech_time_clock)
        return 'time_clock';
    return 'group_classes';
}
// Whether/how the box collects חשבוניות (invoices) from freelance coaches before
// they approve their salary:
//   none      — don't collect at all (coaches aren't freelancers issuing invoices)
//   optional  — coaches may attach an invoice but can approve without one
//   required  — coaches must attach an invoice (file OR url) before approving
export const InvoicePolicySchema = z.enum(['none', 'optional', 'required']);
// Whether an uploaded חשבונית's amount is checked against the salary the coach
// is actually owed (the invoice total, VAT included, BEFORE ניכוי מס במקור):
//   none  — don't read the invoice at all
//   warn  — read it and show the coach a warning on a mismatch; approval still allowed
//   block — a confidently-read mismatching amount blocks the approval
// Only meaningful when invoice_policy !== 'none'. See invoice-check.ts for the
// comparison itself (and why an unreadable invoice never blocks).
export const InvoiceAmountCheckSchema = z.enum(['none', 'warn', 'block']);
// Which optional count columns the emailed Excel includes. No longer owner-
// configurable (simplified) — a fixed default set is always used.
export const ExcelColumnsConfigSchema = z.object({
    registered_count: z.boolean(),
    checkin_count: z.boolean(),
    late_cancel_count: z.boolean(),
});
// The fixed Excel column set used for every attachment (was a box setting).
export const DEFAULT_EXCEL_COLUMNS = {
    registered_count: true,
    checkin_count: true,
    late_cancel_count: false,
};
// Per-box commercial plan (set by Arbox internally, NOT owner-editable):
//   seat_based  — full access; each monthly run meters distinct coaches sent → billed
//   basic       — report + settings + close runs + exports + accountant report,
//                 but the send-to-coach approval flow is disabled
//   deactivated — the whole add-on is blocked (app shows a "contact Arbox" screen)
export const BoxPlanSchema = z.enum(['seat_based', 'basic', 'deactivated']);
// New + existing boxes default to seat_based so nothing is blocked until we
// deliberately downgrade a box.
export const DEFAULT_BOX_PLAN = 'seat_based';
/** Whether this plan may send salary approvals to coaches (only seat_based). */
export function planAllowsSending(plan) {
    return plan === 'seat_based';
}
/** Whether the add-on is usable at all (anything but deactivated). */
export function planIsActive(plan) {
    return plan !== 'deactivated';
}
/** When a hugim receipt's add-ons are paid to the coach:
 *  - spread     — divided evenly over the course's own months (default)
 *  - next_month — in full, in the payroll month after the receipt's */
export const AddonsPaymentMethodSchema = z.enum(['spread', 'next_month']);
/** "רישום לקורס שטרם התחיל" — when a hugim payment made BEFORE the course starts
 *  begins paying the coach (one-off course payments, חוג לכל ילד, spread
 *  add-ons; a הוראת קבע instalment is never affected):
 *  - course_start — from the course's start month, spread to its end (default)
 *  - charge_date  — from the receipt month, spread to the course's end */
export const PrepaidPayoutStartSchema = z.enum(['course_start', 'charge_date']);
/** "אופן חישוב השכר" — which column of Arbox's "הכנסות מחוגים (לפי תאריך
 *  תשלום)" report the course percentage is applied to (hugim only). The
 *  spread, the percentage levels and the add-ons track are unchanged:
 *  - receipt_amount — סכום בקבלה (`amount`, less add-ons) — default, the
 *                     behaviour before this setting existed
 *  - item_price     — מחיר הפריט (`selling_price`), the full list price
 *  - value          — שווי (`paid` = list price less the fixed discount)
 *  - actually_paid  — שולם בפועל (`actually_paid`)
 *  - actually_paid_plus_discount — שולם בפועל בתוספת הנחה: `actually_paid`
 *                     with the (prorated) discount added back
 *  Field map + refund rules: `receiptCourseBase` in hugim-report.service. */
export const HugimPayBaseSchema = z.enum([
    'receipt_amount',
    'item_price',
    'value',
    'actually_paid',
    'actually_paid_plus_discount',
]);
// Box-level payroll settings — deliberately minimal. Pay period is always a
// calendar month, auto-send is off, the email always carries the breakdown
// attachments; none of those are owner-configurable for now. The email's intro +
// signature text are editable (see coach-email.ts); its core is fixed.
const BoxSettingsBaseSchema = z.object({
    // Always calendar_month for now (no custom-period UI). Kept as a config so the
    // period resolver stays unchanged and custom can return later.
    pay_period: PayPeriodConfigSchema,
    // Reply-to for approval emails sent to staff (the owner's address).
    reply_to_email: z.string().email().nullable(),
    // LEGACY — see BoxTypeSchema. Superseded by mech_* below.
    box_type: BoxTypeSchema.default('group_classes'),
    // Independently-toggleable pay mechanisms this box runs (see BoxMechanismSchema).
    // Existing boxes default group_classes on / others off, matching their legacy
    // box_type (see migration 038's backfill).
    mech_group_classes: z.boolean().default(true),
    mech_hugim: z.boolean().default(false),
    mech_time_clock: z.boolean().default(false),
    // Default class calc method (only meaningful for box_type === 'group_classes').
    class_calc_method: ClassCalcMethodSchema,
    // חשבונית collection policy (set in onboarding + settings).
    invoice_policy: InvoicePolicySchema,
    // Whether an uploaded חשבונית's amount is verified against the owed total.
    // Off by default → existing boxes are unaffected.
    invoice_amount_check: InvoiceAmountCheckSchema.default('none'),
    // The box's ח״פ — who a coach's invoice must be addressed to (checked by the
    // invoice check). A P&L unit's own ח״פ, when set, wins for that unit's lines.
    business_id: NullableBusinessId,
    // Where the closed-run report is emailed (the bookkeeper/accountant who pays).
    accountant_email: z.string().email().nullable(),
    // Days from send within which a coach may approve + upload an invoice. The
    // concrete deadline (sent_at + N days) is frozen per line and shown in the
    // email/page; after it the approve/upload endpoints are blocked. null = no
    // deadline (the link never expires by time).
    approval_deadline_days: z.number().int().min(1).max(90).nullable(),
    // When on, a coach's approved invoice is forwarded to the Expenses add-on (if
    // the box also uses Expenses). Off by default.
    forward_invoices_to_expenses: z.boolean().default(false),
    // ── חוג לכל ילד (per-child subsidised course fee), hugim boxes only ───────
    // Off by default → existing boxes are unaffected.
    per_child_fee_enabled: z.boolean().default(false),
    // The single ₪ amount the family actually pays under the subsidy (e.g. 200).
    // It is the IDENTIFIER: a one-time hugim receipt collected for exactly this
    // amount is a חוג-לכל-ילד case. Null until configured; required to enable.
    per_child_fee_amount: z.number().positive().nullable().default(null),
    // The coach's cut of the חוג's FULL price on those receipts (not of the amount
    // above). Its own percentage — the per-hug/per-coach ladder doesn't apply.
    per_child_fee_percent: z.number().min(0).max(100).default(0),
    // ── מוצרים נלווים (add-ons), hugim boxes only ─────────────────────────────
    // When the coach's cut of a receipt's add-ons is paid — see
    // AddonsPaymentMethodSchema. The percentages live in their own table
    // (hugim_addon_percent_settings), like the course percentages.
    addons_payment_method: AddonsPaymentMethodSchema.default('spread'),
    // When a payment made before the course started begins paying — see
    // PrepaidPayoutStartSchema. Hugim boxes only.
    prepaid_payout_start: PrepaidPayoutStartSchema.default('course_start'),
    // Which report column the course percentage applies to — see
    // HugimPayBaseSchema. Hugim boxes only.
    hugim_pay_base: HugimPayBaseSchema.default('receipt_amount'),
    // Whether paid trial sessions (Arbox payment_type 'trial', סוג תשלום "מפגש
    // ניסיון") are paid like any receipt. Off → they are left out of the report
    // and the pay. Hugim boxes only.
    hugim_include_trials: z.boolean().default(true),
    // Whether paid one-time sessions (Arbox payment_type 'onetime', סוג תשלום
    // "מפגש חד פעמי") are paid like any receipt. Off → they are left out of the
    // report and the pay. Hugim boxes only.
    hugim_include_onetime: z.boolean().default(true),
    // ── Coach email text (see coach-email.ts) ────────────────────────────────
    // Opening paragraph under the fixed greeting; null/blank = the default
    // sentence. Signature at the bottom; null/blank = none. The totals, approve
    // button and deadline notice between them are never editable.
    coach_email_intro: z.string().max(COACH_EMAIL_TEXT_MAX).nullable().default(null),
    coach_email_signature: z.string().max(COACH_EMAIL_TEXT_MAX).nullable().default(null),
});
/** Cross-field rules, enforced here (not just in the drawer) so the API can't be
 *  talked into an invalid-but-live setting. */
export const BoxSettingsSchema = BoxSettingsBaseSchema.superRefine((s, ctx) => {
    // A box that runs no mechanism at all pays nobody — every staff member's
    // mechanism set must be a subset of what's enabled here.
    if (!s.mech_group_classes && !s.mech_hugim && !s.mech_time_clock) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mech_group_classes'],
            message: 'לפחות מנגנון שכר אחד חייב להיות פעיל',
        });
    }
    // The identifying amount is what makes the חוג-לכל-ילד rule able to recognize
    // a receipt at all, so the setting can't be turned on without one —
    // otherwise it would be live but silently match nothing.
    if (s.per_child_fee_enabled && !(Number(s.per_child_fee_amount) > 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['per_child_fee_amount'],
            message: 'יש להזין את הסכום שמשלם הלקוח עבור חוג לכל ילד',
        });
    }
    // A mistyped variable would go out verbatim to every coach — refuse it here.
    for (const field of ['coach_email_intro', 'coach_email_signature']) {
        const unknown = unknownCoachEmailVariables(s[field]);
        if (unknown.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `משתנה לא מוכר בתוכן המייל: ${unknown.map((u) => `{${u}}`).join(', ')}`,
            });
        }
    }
});
// Sensible defaults seeded for a new box.
export const DEFAULT_BOX_SETTINGS = {
    pay_period: { mode: 'calendar_month', custom_start_day: null },
    reply_to_email: null,
    box_type: 'group_classes',
    mech_group_classes: true,
    mech_hugim: false,
    mech_time_clock: false,
    class_calc_method: 'checkin',
    invoice_policy: 'optional',
    invoice_amount_check: 'none',
    business_id: null,
    accountant_email: null,
    approval_deadline_days: null,
    forward_invoices_to_expenses: false,
    per_child_fee_enabled: false,
    per_child_fee_amount: null,
    per_child_fee_percent: 0,
    addons_payment_method: 'spread',
    prepaid_payout_start: 'course_start',
    hugim_pay_base: 'receipt_amount',
    hugim_include_trials: true,
    hugim_include_onetime: true,
    coach_email_intro: null,
    coach_email_signature: null,
};
//# sourceMappingURL=box-settings.js.map